import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Coins, BadgeCheck, RotateCcw, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

const FIELDS = [
    {
        section: "Agent kickback rates",
        kicker: "Paid to agents for originating revenue. Adjust freely — takes effect immediately for all future closes.",
        icon: Coins,
        items: [
            { key: "kickback_jv_origination_pct", label: "JV co-origination", suffix: "%", help: "% of admin fee on agent-originated JV close" },
            { key: "kickback_concierge_pct", label: "Concierge tier (Bronze/Silver)", suffix: "%", help: "% of tier price on agent-originated tier purchase" },
            { key: "kickback_concierge_gold_pct", label: "Concierge tier (Gold agents)", suffix: "%", help: "Higher rate to incentivise Gold-tier upgrades" },
            { key: "kickback_jv_trail_pct", label: "JV trail royalty", suffix: "%", help: "Recurring % on follow-on admin fees from same JV" },
        ],
    },
    {
        section: "Verified Agent tier prices",
        kicker: "Annual subscription pricing for the trust badge + perks.",
        icon: BadgeCheck,
        items: [
            { key: "agent_tier_bronze_usd", label: "Bronze (free)", suffix: "USD/yr", help: "Government ID + REA Fiji license check" },
            { key: "agent_tier_silver_usd", label: "Silver", suffix: "USD/yr", help: "+ Reference checks, listing-quality audit, search boost" },
            { key: "agent_tier_gold_usd", label: "Gold", suffix: "USD/yr", help: "+ Dedicated relationship manager, monthly digest, higher concierge split" },
        ],
    },
];

export default function AdminPlatformSettingsTab() {
    const [settings, setSettings] = useState(null);
    const [draft, setDraft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/platform/settings");
            setSettings(res.data);
            setDraft({ ...res.data });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not load settings");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const update = (k, v) => setDraft((d) => ({ ...d, [k]: v === "" ? null : Number(v) }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {};
            for (const sec of FIELDS) {
                for (const it of sec.items) {
                    if (draft[it.key] !== settings[it.key] && draft[it.key] !== null && !Number.isNaN(draft[it.key])) {
                        payload[it.key] = draft[it.key];
                    }
                }
            }
            if (Object.keys(payload).length === 0) {
                toast.info("No changes to save.");
                setSaving(false);
                return;
            }
            const res = await api.patch("/admin/platform/settings", payload);
            setSettings(res.data);
            setDraft({ ...res.data });
            toast.success("Settings saved — takes effect immediately.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const reset = () => {
        setDraft({ ...settings });
        toast.info("Reverted to last saved values.");
    };

    if (loading || !draft) {
        return (
            <div className="text-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-coral mx-auto" />
            </div>
        );
    }

    const dirty = settings && Object.keys(settings).some((k) => settings[k] !== draft[k]);

    return (
        <div className="space-y-6" data-testid="admin-platform-settings-tab">
            <Card className="p-5 bg-coral/5 dark:bg-coral/10 border-coral/30">
                <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-coral mt-0.5 shrink-0" />
                    <div>
                        <div className="font-display text-base font-bold text-foreground mb-1">Platform settings</div>
                        <p className="font-app text-sm text-muted-foreground leading-relaxed">
                            Tune agent kickback rates and Verified Agent tier prices on the fly. Changes apply
                            immediately to all future closes and tier purchases — historical payouts are unaffected.
                        </p>
                    </div>
                </div>
            </Card>

            {FIELDS.map((sec) => {
                const Icon = sec.icon;
                return (
                    <Card key={sec.section} className="p-5 lg:p-6 bg-card" data-testid={`admin-settings-section-${sec.section.toLowerCase().replace(/\s+/g, "-")}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                            <Icon className="h-4 w-4 text-coral" />
                            <h3 className="font-display text-base font-bold text-foreground">{sec.section}</h3>
                        </div>
                        <p className="font-app text-xs text-muted-foreground mb-4">{sec.kicker}</p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            {sec.items.map((f) => (
                                <div key={f.key}>
                                    <Label htmlFor={`set-${f.key}`} className="font-app text-xs text-muted-foreground">
                                        {f.label}
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id={`set-${f.key}`}
                                            type="number"
                                            step="0.5"
                                            min="0"
                                            value={draft[f.key] ?? ""}
                                            onChange={(e) => update(f.key, e.target.value)}
                                            data-testid={`set-${f.key}`}
                                            className="bg-background text-foreground pr-16"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-app text-muted-foreground pointer-events-none">
                                            {f.suffix}
                                        </span>
                                    </div>
                                    <p className="font-app text-[11px] text-muted-foreground mt-1">{f.help}</p>
                                </div>
                            ))}
                        </div>
                    </Card>
                );
            })}

            <div className="sticky bottom-3 z-10 flex flex-wrap gap-2 justify-end p-3 rounded-md bg-background/95 backdrop-blur border border-border shadow">
                <Button
                    variant="outline"
                    onClick={reset}
                    disabled={!dirty || saving}
                    data-testid="admin-settings-reset-btn"
                    className="font-app"
                >
                    <RotateCcw className="h-3.5 w-3.5 mr-2" /> Revert
                </Button>
                <Button
                    onClick={save}
                    disabled={!dirty || saving}
                    data-testid="admin-settings-save-btn"
                    className="bg-coral hover:bg-coral-deep text-white font-app"
                >
                    {saving ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-2" />}
                    {saving ? "Saving…" : dirty ? "Save changes" : "Up to date"}
                </Button>
            </div>
        </div>
    );
}
